import { useEffect, useMemo, useRef, useState } from "react";
import { X, Save, ChevronDown, Trash2, Check, X as XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { EditableText } from "../Common/EditableText";
import { User } from "../../../types/slice.types";
import { useDispatch } from "react-redux";
import { Field, ReadOnly } from "../Common/CardUIHelpers";
import {
  deleteUser,
  updateUser,
} from "../../../redux/slices/admin.slices/users.slice";

export function UserDetailsOverlay({
  user,
  onClose,
}: {
  user: User;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [form, setForm] = useState<User>(user);
  const [showRolePopup, setShowRolePopup] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const roleRef = useRef<HTMLDivElement | null>(null);
  const deleteRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showRolePopup &&
        roleRef.current &&
        !roleRef.current.contains(e.target as Node)
      ) {
        setShowRolePopup(false);
      }

      if (
        showDeleteConfirm &&
        deleteRef.current &&
        !deleteRef.current.contains(e.target as Node)
      ) {
        setShowDeleteConfirm(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showRolePopup, showDeleteConfirm]);

  const hasChanges = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(user),
    [form, user]
  );

  const handleSave = () => {
    if (!hasChanges) return;
    dispatch(updateUser({ user: form }) as any);
    onClose();
  };

  const handleDelete = () => {
    dispatch(deleteUser({ userId: user._id }) as any);
    setShowDeleteConfirm(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative mt-20 w-full max-w-3xl bg-white dark:bg-gray-900 rounded-xl shadow-xl">
        <div className="flex items-center justify-between p-6">
          <h2 className="text-xl font-semibold">{t("User Details")}</h2>

          <div className="flex items-center gap-2 relative">
            <button
              onClick={() => setShowDeleteConfirm((p) => !p)}
              className="p-2 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 transition"
            >
              <Trash2 size={18} className="text-red-600" />
            </button>

            {showDeleteConfirm && (
              <div
                ref={deleteRef}
                className="absolute right-0 top-12 z-50 w-64 rounded-lg bg-white dark:bg-gray-800 shadow-xl p-4"
              >
                <p className="text-sm mb-4">
                  {t("Do you want to delete this user?")}
                </p>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                  >
                    <XIcon size={18} />
                  </button>

                  <button
                    onClick={handleDelete}
                    className="p-2 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 transition"
                  >
                    <Check size={18} className="text-red-600" />
                  </button>
                </div>
              </div>
            )}

            <button
              disabled={!hasChanges}
              onClick={handleSave}
              className={`p-2 rounded-full transition ${
                hasChanges
                  ? "hover:bg-gray-200 dark:hover:bg-gray-700"
                  : "opacity-40 cursor-not-allowed"
              }`}
            >
              <Save size={18} />
            </button>

            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto custom-scroll">
          <Field label={t("Name")}>
            <EditableText
              value={form.name}
              onChange={(v) => setForm((p) => ({ ...p, name: v }))}
            />
          </Field>

          <Field label={t("Email")}>
            <EditableText
              value={form.email}
              onChange={(v) => setForm((p) => ({ ...p, email: v }))}
            />
          </Field>

          <Field label={t("Phone")}>
            <EditableText
              value={form.phone}
              onChange={(v) => setForm((p) => ({ ...p, phone: v }))}
            />
          </Field>

          <Field label={t("Role")}>
            <div className="relative">
              <button
                onClick={() => setShowRolePopup((p) => !p)}
                className="flex items-center gap-2 px-3 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                {form.role}
                <ChevronDown size={14} />
              </button>

              {showRolePopup && (
                <div
                  ref={roleRef}
                  className="absolute left-0 top-10 z-50 w-32 rounded-lg bg-white dark:bg-gray-800 shadow-lg"
                >
                  {["user", "admin"].map((role) => (
                    <button
                      key={role}
                      onClick={() => {
                        setForm((p) => ({ ...p, role }));
                        setShowRolePopup(false);
                      }}
                      className={`w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition ${
                        form.role === role ? "font-semibold" : ""
                      }`}
                    >
                      {role}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Field>

          <ReadOnly label={t("City")} value={user.city} />
          <ReadOnly label={t("Cars")} value={user.cars} />
          <ReadOnly label={t("Bookings")} value={user.bookings} />
          <ReadOnly label={t("Orders")} value={user.orders} />
          <ReadOnly label={t("User ID")} value={user._id} />
        </div>
      </div>
    </div>
  );
}
